import React from 'react';

import { postWebUiApi } from '../../api';
import { WebUiAlert } from '../../components/alert';
import WebUiPageWrapper from '../../components/page-wrapper';
import PortsConfigTable from './ports/PortsConfigTable';
import PortsGeneralConfig from './ports/PortsGeneralConfig';
import PortsStatusTable from './ports/PortsStatusTable';
import type { InterfaceCfg, InterfaceState } from './ports/types';

type BspState = { bsp: boolean };

export default function WebUiSettingsPortsPage() {
  const [state, setState] = React.useState<InterfaceState[]>([]);
  const [cfg, setCfg] = React.useState<InterfaceCfg[]>([]);
  const [bsp, setBsp] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [message, setMessage] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const updateCfg = React.useCallback(
    (id: number, patch: Partial<InterfaceCfg>) => {
      setCfg((prev) =>
        prev.map((item) =>
          item.interface === id ? { ...item, ...patch } : item,
        ),
      );
    },
    [],
  );

  React.useEffect(() => {
    let alive = true;

    const loadConfig = async () => {
      const [bspResp, cfgResp] = await Promise.all([
        postWebUiApi<BspState>({
          service: 'interface',
          access: 'get',
          command: 'get bsp state',
        }),
        postWebUiApi<InterfaceCfg[]>({
          service: 'interface',
          access: 'get',
          command: 'get interfaces',
        }),
      ]);
      if (!alive) return;
      setBsp(Boolean(bspResp.bsp));
      setCfg(
        cfgResp.map((item) => ({ ...item, speed: Math.min(item.speed, 100) })),
      );
    };

    const loadState = async () => {
      const stateResp = await postWebUiApi<InterfaceState[]>({
        service: 'interface',
        access: 'get',
        command: 'get interfaces state',
      });
      if (!alive) return;
      setState(stateResp);
    };

    const loadAll = async () => {
      try {
        await Promise.all([loadConfig(), loadState()]);
        if (alive) setError(null);
      } catch (err) {
        if (alive) setError((err as Error).message);
      }
    };

    loadAll();
    const timer = window.setInterval(() => loadState().catch(() => {}), 1000);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, []);

  const onSaveGeneral = React.useCallback(async () => {
    try {
      setSaving(true);
      await postWebUiApi({
        service: 'interface',
        access: 'set',
        command: 'set bsp state',
        parameters: { bsp },
      });
      setMessage('General configuration updated.');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }, [bsp]);

  const onSavePorts = React.useCallback(async () => {
    try {
      setSaving(true);
      await postWebUiApi({
        service: 'interface',
        access: 'set',
        command: 'set interfaces',
        parameters: cfg,
      });
      setMessage('Port configuration updated.');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }, [cfg]);

  return (
    <WebUiPageWrapper titleList={['Settings', 'Ports']}>
      {message ? <WebUiAlert type="success" message={message} /> : null}
      {error ? <WebUiAlert type="error" message={error} /> : null}
      <div className="flex flex-col gap-2">
        <div className="flex w-full flex-row gap-2">
          <PortsStatusTable state={state} />
          <PortsGeneralConfig
            bsp={bsp}
            setBsp={setBsp}
            saving={saving}
            onSave={onSaveGeneral}
          />
        </div>
        <div className="w-full">
          <PortsConfigTable
            cfg={cfg}
            saving={saving}
            updateCfg={updateCfg}
            onSave={onSavePorts}
          />
        </div>
      </div>
    </WebUiPageWrapper>
  );
}
